import { useSelector } from 'react-redux';
import { useState, useEffect, useRef } from 'react';
import { logout, getUserProfile } from '../../services/auth';
import { invokeFunction } from '../../services/functions';
import { Link } from 'react-router-dom';
import { getExchangeRate } from '../../services/settings';
import { getTodayRate } from '../../services/exchangeRates';
import DarkModeSwitcher from './DarkModeSwitcher';

import CommandPalette from '../../components/commons/CommandPalette';
import LogoBlue from '../../images/logo/logo_1.png';
import LogoWhite from '../../images/logo/logo_3.png';
import Imagotipo from '../../images/logo/imagotipo.png';


const Header = (props: {
  sidebarOpen: string | boolean | undefined;
  setSidebarOpen: (arg0: boolean) => void;
  toggleSidebarCollapse?: () => void;
  isSidebarCollapsed?: boolean;
  onOpenNotes?: () => void;
}) => {
  const { session } = useSelector((state: any) => state.auth);
  const user = session?.user;

  const [userProfile, setUserProfile] = useState<{ first_name?: string, last_name?: string, nickname?: string, avatar_url?: string } | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [currentTc, setCurrentTc] = useState(3.39);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Load current exchange rate for the header ticker
    try {
      const p = getExchangeRate?.();
      if (p && typeof p.then === 'function') {
        p.then(rate => { if (rate) setCurrentTc(rate); }).catch(() => {});
      }
    } catch (e) {
      console.warn('Header: Failed to fetch exchange rate', e);
    }


    // Auto-sync: if today's rate doesn't exist in DB, fetch via Edge Function
    (async () => {
      try {
        const todayRate = await getTodayRate();
        if (todayRate) return; // Already exists, nothing to do

        // Call Edge Function (server-side proxy to SUNAT API)
        const { data } = await invokeFunction('fetch-sunat-tc');
        if (data?.compra && data?.venta) {
          setCurrentTc(data.venta);
          console.log(`[AutoSync] TC del día registrado: C ${data.compra} / V ${data.venta}`);
        }
      } catch (e) {
        console.warn('[AutoSync] No se pudo auto-sincronizar TC:', e);
      }
    })();
  }, []);

  useEffect(() => {
    if (user?.id) {
      getUserProfile(user.id)
        .then(({ data }) => {
          if (data) setUserProfile(data);
        });
    }
  }, [user?.id]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownOpen || dropdownRef.current.contains(target as Node)) return;
      setDropdownOpen(false);
    };
    document.addEventListener("mousedown", clickHandler);
    return () => document.removeEventListener("mousedown", clickHandler);
  });

  const firstName = userProfile?.first_name || user?.user_metadata?.first_name || 'Usuario';
  const lastName = userProfile?.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const initials = firstName.charAt(0).toUpperCase() + (lastName ? lastName.charAt(0).toUpperCase() : '');
  const email = user?.email || '';

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <>
      <CommandPalette />
      <header className="sticky top-0 z-999 flex flex-col w-full bg-white drop-shadow-1 dark:bg-boxdark dark:drop-shadow-none">
        {/* Main Header Row */}
        <div className="flex items-center h-14 px-4 md:px-6 2xl:px-8">

          {/* LEFT GROUP: Sidebar toggle + Logo */}
          <div className="flex items-center gap-1">
            {/* Mobile Hamburger */}
            <button
              aria-controls="sidebar"
              onClick={(e) => {
                e.stopPropagation();
                props.setSidebarOpen(!props.sidebarOpen);
              }}
              className="flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-meta-4 transition-colors lg:hidden"
            >
              <span className="material-symbols-outlined text-[22px]">menu</span>
            </button>

            {/* Desktop Sidebar Collapse Toggle */}
            <button
              onClick={props.toggleSidebarCollapse}
              className="hidden lg:flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-meta-4 transition-colors"
              title={props.isSidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            >
              <span className="material-symbols-outlined text-[22px]">
                {props.isSidebarCollapsed ? 'menu' : 'menu_open'}
              </span>
            </button>

            {/* Separator */}
            <div className="h-6 w-px bg-slate-200 dark:bg-strokedark mx-1 hidden sm:block"></div>

            {/* Logo in Header */}
            <Link to="/" className="flex items-center group">
              {/* Light mode: blue logo */}
              <img src={LogoBlue} alt="Ruta Mundial" className="hidden sm:block dark:sm:hidden h-8 transition-all group-hover:opacity-90" />
              {/* Dark mode: white logo */}
              <img src={LogoWhite} alt="Ruta Mundial" className="hidden dark:sm:block h-8 transition-all group-hover:opacity-90" />
              {/* Mobile: just the icon */}
              <img src={Imagotipo} alt="Ruta Mundial" className="block sm:hidden w-8 h-8" />
            </Link>
          </div>

          {/* CENTER GROUP: Search + TC + Quick Actions */}
          <div className="hidden md:flex items-center gap-3 flex-1 justify-center px-6">
            {/* Global Search Trigger (Cmd+K) */}
            <button
              onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
              className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200/80 dark:bg-meta-4 dark:hover:bg-boxdark-2 px-4 py-2 rounded-xl text-slate-500 dark:text-slate-400 text-[13px] transition-colors min-w-[220px] max-w-[320px] cursor-text group"
            >
              <span className="material-symbols-outlined text-[18px] group-hover:text-[#3245a4] transition-colors">search</span>
              <span className="flex-1 text-left">Buscar...</span>
              <kbd className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-white dark:bg-boxdark rounded border border-slate-200 dark:border-strokedark text-[10px] font-mono text-slate-400">
                ⌘K
              </kbd>
            </button>

            <div className="h-6 w-px bg-slate-200 dark:bg-strokedark"></div>

            {/* Ticker TC */}
            <div className="hidden lg:flex items-center gap-1.5 text-[12px] font-bold text-emerald-600 dark:text-emerald-400">
              <span className="bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">USD 1.00</span>
              <span className="material-symbols-outlined text-[13px]">sync_alt</span>
              <span className="bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">S/ {currentTc.toFixed ? currentTc.toFixed(2) : currentTc}</span>
            </div>

            <div className="h-6 w-px bg-slate-200 dark:bg-strokedark hidden lg:block"></div>

            {/* Quick Actions */}
            <div className="hidden lg:flex items-center gap-2">
              <Link to="/customers" className="flex items-center gap-1.5 hover:bg-slate-100 dark:hover:bg-meta-4 text-slate-600 dark:text-slate-300 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors">
                <span className="material-symbols-outlined text-[16px]">person_add</span>
                Nuevo Cliente
              </Link>
              <Link to="/cotizations" className="flex items-center gap-1.5 bg-[#3245a4] hover:bg-[#283d8f] text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-colors">
                <span className="material-symbols-outlined text-[16px]">add_task</span>
                Nueva Reserva
              </Link>
            </div>
          </div>

          {/* RIGHT GROUP: Actions + Profile */}
          <div className="flex items-center gap-2 ml-auto">
            {/* Mobile Search */}
            <button
              onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
              className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-meta-4 transition-colors"
            >
              <span className="material-symbols-outlined text-[20px]">search</span>
            </button>

            {/* Notes Button */}
            {props.onOpenNotes && (
              <button
                onClick={props.onOpenNotes}
                className="flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-[#0B357F]/10 dark:hover:bg-[#0B357F]/20 hover:text-[#0B357F] dark:hover:text-blue-300 transition-colors"
                title="Notas internas"
              >
                <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>edit_note</span>
              </button>
            )}

            <DarkModeSwitcher />

            {/* Indicador de Estado de Conexión Pulsante */}
            <div 
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-50 dark:bg-meta-4/20 text-xs font-semibold select-none cursor-help" 
              title={isOnline ? "Conectado a la red en vivo. Datos 100% actualizados." : "Sin conexión a internet. Modo de emergencia activo."}
            >
              <span className="relative flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isOnline ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
                <span className={`relative inline-flex rounded-full h-2 w-2 ${isOnline ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
              </span>
              <span className={`hidden sm:inline ${isOnline ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {isOnline ? 'En línea' : 'Sin conexión'}
              </span>
            </div>

            <div className="h-6 w-px bg-slate-200 dark:bg-strokedark"></div>

            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2.5 p-1 rounded-lg hover:bg-slate-50 dark:hover:bg-meta-4 transition-colors focus:ring-2 focus:ring-[#3245a4]/20 focus:outline-none"
              >
                <div className="w-8 h-8 rounded-full bg-[#0052CC] text-white flex items-center justify-center font-bold text-[13px] overflow-hidden shrink-0 shadow-sm ring-2 ring-transparent hover:ring-[#3245a4]/20 transition-all">
                  {userProfile?.avatar_url ? (
                    <img src={userProfile.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                  ) : initials}
                </div>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 flex w-[280px] flex-col rounded-2xl bg-white dark:bg-boxdark shadow-[0px_8px_24px_rgba(9,30,66,0.15)] dark:shadow-none border border-[#DFE1E6] dark:border-strokedark z-[99999] overflow-hidden animate-in slide-in-from-top-2 duration-150">
                  <div className="px-5 py-4 border-b border-slate-100 dark:border-strokedark bg-[#F4F5F7]/30 dark:bg-meta-4/30">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-[#0052CC] text-white flex items-center justify-center font-bold text-base overflow-hidden shrink-0 shadow-sm">
                        {userProfile?.avatar_url ? (
                          <img src={userProfile.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                        ) : initials}
                      </div>
                      <div className="flex flex-col truncate">
                        <span className="text-sm font-bold text-[#172B4D] dark:text-white truncate">{fullName}</span>
                        <span className="text-[11px] font-medium text-[#5E6C84] dark:text-slate-400 truncate">{email}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 flex flex-col gap-0.5">
                    <Link
                      to="/profile"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-[#172B4D] dark:text-slate-200 rounded-lg hover:bg-[#F4F5F7] dark:hover:bg-meta-4 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[18px] text-[#42526E] dark:text-slate-400">person</span>
                      Mi Perfil
                    </Link>
                    <Link
                      to="/commissions"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-[#172B4D] dark:text-slate-200 rounded-lg hover:bg-[#F4F5F7] dark:hover:bg-meta-4 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[18px] text-[#42526E] dark:text-slate-400">paid</span>
                      Mis Comisiones
                    </Link>
                  </div>

                  <div className="h-px w-full bg-[#DFE1E6] dark:bg-strokedark"></div>

                  <div className="p-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-[#DE350B] dark:text-meta-1 rounded-lg hover:bg-[#FFEBE6] dark:hover:bg-meta-1/10 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[18px]">logout</span>
                      Cerrar Sesión
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>


      </header>
    </>
  );
};

export default Header;